import { undashifyAndCapitalise } from '@cinch-labs/shared-util'
import { getStoryBySlug } from '@cinch-nx/data-storyblok'
import { ArticleStoryContent } from '@cinch-nx/landing-ui-articles'
import { GetStaticPropsContext } from 'next'
import { tagError, setTag, traceFunction } from '@core/datadog-server'

const instrumentedGetStoryBySlug = traceFunction(
  'getStoryBySlug',
  getStoryBySlug,
)

interface breadCrumbs {
  text: string
  url: string
}

export interface GetStaticPropsGeneratorArg {
  path: string
  resolveRelations: string
  passedBreadCrumbs: breadCrumbs[]
  shouldUndashifyAndCapitalise: boolean
}

export const getStaticPropsGenerator =
  ({
    path,
    resolveRelations,
    passedBreadCrumbs,
    shouldUndashifyAndCapitalise,
  }: GetStaticPropsGeneratorArg) =>
  async ({ params, preview = false }: GetStaticPropsContext) => {
    const slug = params?.slug as string
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const story = await instrumentedGetStoryBySlug<ArticleStoryContent>(
        `${path}/${slug}`,
        {
          version: preview ? 'draft' : 'published',
          resolve_relations: resolveRelations,
        },
      )

      const breadCrumbs = [
        ...passedBreadCrumbs,
        shouldUndashifyAndCapitalise && {
          text: undashifyAndCapitalise(slug),
          url: `/${path}/${slug}`,
        },
      ]

      return {
        props: {
          story,
          preview,
          layoutProps: {
            showBorderRadius: true,
            breadCrumbs,
          },
        },
        revalidate: 60, // In seconds
      }
    } catch (error) {
      if (typeof error === 'string' || error instanceof Error) {
        tagError(error)
      }
      setTag('response.statusCode', 404)
      console.log(error)
      return {
        notFound: true,
        props: {
          story: null,
          preview,
          layoutProps: {
            showBorderRadius: true,
            breadCrumbs: [],
          },
        },
        revalidate: 60, // In seconds
      }
    }
  }

export type ArticlesFeatureProps = Extract<
  Awaited<ReturnType<ReturnType<typeof getStaticPropsGenerator>>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { props: any }
>['props']
